import React, { useState, useEffect, useCallback } from "react";
import {
    Button,
    Table,
    Modal,
    Form,
    Input,
    Select,
    message,
    Typography,
    Card,
    Row,
    Popconfirm,
    Drawer,
    Space,
    DatePicker,
    Col
} from "antd";
import TrashIcon from '../../assets/icons/trash.svg';
import PenIcon from '../../assets/icons/pen.svg';
import { formatDate, openNotification } from "../../utils";
import { SearchOutlined } from '@ant-design/icons';
import AnnouncementService from "../../services/AnnouncementService";
import dayjs from 'dayjs';
import { APP_LOCAL_DATE_FORMAT } from 'config/constants';
import type { ColumnsType } from 'antd/es/table';
import type { SortOrder, SorterResult, TablePaginationConfig } from 'antd/es/table/interface';

const { Option } = Select;
const { Title } = Typography;
const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

/**
 * Enum Değerlerini -> Türkçe metinlere dönüştürmek için bir yardımcı obje.
 */
const ANNOUNCEMENT_TYPE_LABELS: Record<string, string> = {
    INFORMATION: "Bilgilendirme",
    UPDATE: "Güncelleme",
    EVENT: "Etkinlik",
};

const AnnouncementList = () => {
    const [announcements, setAnnouncements] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState<any | null>(null);
    const [total, setTotal] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [searchInput, setSearchInput] = useState<string>("");
    const [search, setSearch] = useState<string>("");
    const [filterOpen, setFilterOpen] = useState<boolean>(false);
    const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
    const [createdFrom, setCreatedFrom] = useState<string | undefined>(undefined);
    const [createdTo, setCreatedTo] = useState<string | undefined>(undefined);
    const [updatedFrom, setUpdatedFrom] = useState<string | undefined>(undefined);
    const [updatedTo, setUpdatedTo] = useState<string | undefined>(undefined);
    const [pendingType, setPendingType] = useState<string | undefined>(undefined);
    const [pendingCreatedFrom, setPendingCreatedFrom] = useState<any>(null);
    const [pendingCreatedTo, setPendingCreatedTo] = useState<any>(null);
    const [pendingUpdatedFrom, setPendingUpdatedFrom] = useState<any>(null);
    const [pendingUpdatedTo, setPendingUpdatedTo] = useState<any>(null);
    const [form] = Form.useForm();
    const [sortField, setSortField] = useState<string>('updatedAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const loadAnnouncements = useCallback(async () => {
        setLoading(true);
        try {
            const params: Record<string, any> = {
                page: page - 1,
                size: pageSize,
                sort: `${sortField},${sortOrder}`,
            };
            const trimmedSearch = search.trim();
            if (trimmedSearch) {
                params['search.contains'] = trimmedSearch;
            }
            if (typeFilter) {
                params['type.equals'] = typeFilter;
            }
            if (createdFrom) {
                params['createdAt.greaterThanOrEqual'] = createdFrom;
            }
            if (createdTo) {
                params['createdAt.lessThanOrEqual'] = createdTo;
            }
            if (updatedFrom) {
                params['updatedAt.greaterThanOrEqual'] = updatedFrom;
            }
            if (updatedTo) {
                params['updatedAt.lessThanOrEqual'] = updatedTo;
            }

            const response = await AnnouncementService.getAllAnnouncements(params);
            const pageData = response?.data;
            const content = pageData?.content ?? [];
            const totalElements = pageData?.totalElements ?? content.length ?? 0;
            setAnnouncements(content);
            setTotal(totalElements);
        } catch (error) {
            message.error("Duyurular yüklenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, search, typeFilter, createdFrom, createdTo, updatedFrom, updatedTo, sortField, sortOrder]);

    useEffect(() => {
        loadAnnouncements();
    }, [loadAnnouncements]);

    const openFilterDrawer = () => {
        setPendingType(typeFilter);
        setPendingCreatedFrom(createdFrom ? dayjs(createdFrom) : null);
        setPendingCreatedTo(createdTo ? dayjs(createdTo) : null);
        setPendingUpdatedFrom(updatedFrom ? dayjs(updatedFrom) : null);
        setPendingUpdatedTo(updatedTo ? dayjs(updatedTo) : null);
        setFilterOpen(true);
    };

    const closeFilterDrawer = () => setFilterOpen(false);

    const clearPendingFilters = () => {
        setPendingType(undefined);
        setPendingCreatedFrom(null);
        setPendingCreatedTo(null);
        setPendingUpdatedFrom(null);
        setPendingUpdatedTo(null);
    };

    const applyFilters = () => {
        setTypeFilter(pendingType);
        setCreatedFrom(
            pendingCreatedFrom
                ? pendingCreatedFrom.startOf('day').format(DATE_TIME_FORMAT)
                : undefined
        );
        setCreatedTo(
            pendingCreatedTo
                ? pendingCreatedTo.endOf('day').format(DATE_TIME_FORMAT)
                : undefined
        );
        setUpdatedFrom(
            pendingUpdatedFrom
                ? pendingUpdatedFrom.startOf('day').format(DATE_TIME_FORMAT)
                : undefined
        );
        setUpdatedTo(
            pendingUpdatedTo
                ? pendingUpdatedTo.endOf('day').format(DATE_TIME_FORMAT)
                : undefined
        );
        setPage(1);
        closeFilterDrawer();
    };

    const handleCreate = () => {
        setEditingAnnouncement(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (announcement: any) => {
        setEditingAnnouncement(announcement);
        // Form değerlerini set ederken enum alanını da dolduruyoruz
        form.setFieldsValue({
            ...announcement,
            // Herhangi bir ek manipülasyon gerekiyorsa burada yapabilirsiniz
        });
        setIsModalVisible(true);
    };

    const handleDelete = async (id: number) => {
        try {
            await AnnouncementService.deleteAnnouncement(id);
            openNotification('success', 'Başarılı', "Duyuru silindi.");
            loadAnnouncements();
        } catch (error: any) {
            if (error.response && error.response.status === 409) {
                openNotification(
                    'error',
                    'Hata',
                    "Silme işlemi gerçekleştirilemez. Bu duyuru aktif olarak kullanılmaktadır."
                );
            } else {
                openNotification('error', 'Hata', "Duyuru silinemedi.");
            }
        }
    };

    const handleSubmit = async (values: any) => {
        try {
            if (editingAnnouncement) {
                // Güncelleme
                await AnnouncementService.updateAnnouncement(editingAnnouncement.id, values);
                openNotification('success', 'Başarılı', "Duyuru güncellendi.");
            } else {
                // Yeni oluşturma
                await AnnouncementService.createAnnouncement(values);
                openNotification('success', 'Başarılı', "Duyuru oluşturuldu.");
            }
            loadAnnouncements();
            setIsModalVisible(false);
        } catch (error) {
            openNotification('error', 'Başarısız', "İşlemde hata meydana geldi.");
        }
    };

    // Tabloda yeni bir sütun ekleyerek enum değerini Türkçe gösteriyoruz:
    const currentSortOrder = (field: string): SortOrder | undefined => {
        if (sortField !== field) {
            return undefined;
        }
        return sortOrder === 'asc' ? 'ascend' : 'descend';
    };

    const columns: ColumnsType<any> = [
        {
            title: "Başlık",
            dataIndex: "header",
            key: "header",
        },
        {
            title: "Açıklama",
            dataIndex: "description",
            key: "description",
        },
        {
            title: "Duyuru Tipi",
            dataIndex: "type",
            key: "type",
            sorter: true,
            sortOrder: currentSortOrder('type'),
            render: (typeValue: string) => ANNOUNCEMENT_TYPE_LABELS[typeValue] || typeValue,
        },
        {
            title: "Oluşturma Tarihi",
            dataIndex: "createdAt",
            key: "createdAt",
            sorter: true,
            sortOrder: currentSortOrder('createdAt'),
            render: (text: any) => <span>{formatDate(text)}</span>,
        },
        {
            title: "Güncellenme Tarihi",
            dataIndex: "updatedAt",
            key: "updatedAt",
            sorter: true,
            sortOrder: currentSortOrder('updatedAt'),
            render: (text: any) => <span>{formatDate(text)}</span>,
        },
        {
            title: "İşlemler",
            key: "actions",
            width: 140,
            render: (_: any, record: any) => (
                <div>
                    <Button
                        shape="circle"
                        icon={<img src={PenIcon} alt="edit" className="icon" />}
                        onClick={() => handleEdit(record)}
                        className="icon-button mr-10px"
                    />
                    <Popconfirm placement="left"
                        title="Bu duyuruyu silmek istediğinize emin misiniz?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <Button
                            shape="circle"
                            icon={<img src={TrashIcon} alt="delete" className="icon" />}
                            className="icon-button"
                        />
                    </Popconfirm>
                </div>
            ),
        },
    ];

    return (
        <>
            <Card
                className="br-30px"
            >
                <Row className="mb-16" align="middle" justify='space-between'>
                    <Col>
                    <Row justify={'start'} align='bottom'>
  <Title style={{ margin: 0 }} level={4}>Duyuru Listesi</Title>
                        <Typography.Text type='secondary' className='ms-5'>
                            <strong>({total})</strong> kayıt listelendi
                        </Typography.Text>
                    </Row>
                      
                    </Col>
                    <Col>
                        <Space>
                            <Input
                                allowClear
                                placeholder="Ara"
                                prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
                                className="d-input d-input-form d-input-filter"
                                style={{ width: 220 }}
                                value={searchInput}
                                onChange={(e) => {
                                    const value = e.target.value || '';
                                    setSearchInput(value);
                                    const normalizedSearch = value
                                        .toLocaleLowerCase('tr-TR')
                                        .replace(/\u0307/g, '')
                                        .replace(/ı/g, 'i');
                                    setSearch(normalizedSearch);
                                    setPage(1);
                                    setSortField('updatedAt');
                                    setSortOrder('desc');
                                }}
                            />
                            <Button type='primary' onClick={openFilterDrawer}>Filtrele</Button>
                            <Button
                                type="primary"
                                className="d-button dark-button width-auto"
                                onClick={handleCreate}
                            >
                                Yeni Duyuru Ekle
                            </Button>
                        </Space>
                    </Col>

                </Row>
                <Table
                style={{ marginTop: 16 }}
                    dataSource={announcements}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: page,
                        pageSize,
                        total,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '30'],
                    }}
                    onChange={(pagination: TablePaginationConfig, _filters, sorter) => {
                        const current = pagination.current ?? 1;
                        const size = pagination.pageSize ?? pageSize;

                        let shouldResetPage = false;

                        if (size !== pageSize) {
                            setPageSize(size);
                            shouldResetPage = true;
                        }

                        const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter;
                        const fieldMap: Record<string, string> = {
                            type: 'type',
                            createdAt: 'createdAt',
                            updatedAt: 'updatedAt',
                        };
                        const typedSorter = sorterResult as SorterResult<any> | undefined;
                        const sorterFieldKey = typedSorter?.field as string | undefined;
                        const nextField = sorterFieldKey ? fieldMap[sorterFieldKey] : undefined;
                        const nextOrder = typedSorter?.order;

                        if (nextField && nextOrder) {
                            const mappedOrder = nextOrder === 'ascend' ? 'asc' : 'desc';
                            if (nextField !== sortField) {
                                setSortField(nextField);
                                shouldResetPage = true;
                            }
                            if (mappedOrder !== sortOrder) {
                                setSortOrder(mappedOrder);
                                shouldResetPage = true;
                            }
                        } else if (nextField && !nextOrder) {
                            if (sortField !== 'updatedAt') {
                                setSortField('updatedAt');
                                shouldResetPage = true;
                            }
                            if (sortOrder !== 'desc') {
                                setSortOrder('desc');
                                shouldResetPage = true;
                            }
                        }

                        if (shouldResetPage) {
                            if (page !== 1) {
                                setPage(1);
                            }
                        } else if (current !== page) {
                            setPage(current);
                        }
                    }}
                />
            </Card>

            <Drawer
                open={filterOpen}
                onClose={closeFilterDrawer}
                destroyOnClose
                width={360}
                title="Filtre"
            >
                <Space direction='vertical' style={{ width: '100%' }} size='large'>
                    <div>
                        <Typography.Text>Duyuru Tipi</Typography.Text>
                        <Select
                            allowClear
                            placeholder="Seçiniz"
                            style={{ width: '100%', marginTop: 8 }}
                            value={pendingType}
                            onChange={(value) => setPendingType(value)}
                        >
                            {Object.entries(ANNOUNCEMENT_TYPE_LABELS).map(([key, label]) => (
                                <Option key={key} value={key}>{label}</Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <Typography.Text>Oluşturma Tarihi</Typography.Text>
                        <DatePicker.RangePicker
                            style={{ width: '100%', marginTop: 6 }}
                            value={[pendingCreatedFrom, pendingCreatedTo] as any}
                            onChange={(values) => {
                                setPendingCreatedFrom(values?.[0] || null);
                                setPendingCreatedTo(values?.[1] || null);
                            }}
                            format={APP_LOCAL_DATE_FORMAT}
                        />
                    </div>
                    <div>
                        <Typography.Text>Güncelleme Tarihi</Typography.Text>
                        <DatePicker.RangePicker
                            style={{ width: '100%', marginTop: 6 }}
                            value={[pendingUpdatedFrom, pendingUpdatedTo] as any}
                            onChange={(values) => {
                                setPendingUpdatedFrom(values?.[0] || null);
                                setPendingUpdatedTo(values?.[1] || null);
                            }}
                            format={APP_LOCAL_DATE_FORMAT}
                        />
                    </div>
                    <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                        <Button onClick={clearPendingFilters}>Temizle</Button>
                        <Button type='primary' onClick={applyFilters}>Uygula</Button>
                    </Space>
                </Space>
            </Drawer>

            <Modal
                title={editingAnnouncement ? "Duyuru Düzenle" : "Yeni Duyuru Ekle"}
                visible={isModalVisible}
                footer={null}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form
                    form={form}
                    onFinish={handleSubmit}
                    layout="vertical"
                // Tüm alanların required olduğunu hatırlatalım.
                >
                    <Form.Item
                        label="Başlık"
                        name="header"
                        rules={[
                            { required: true, message: 'Lütfen duyuru başlığı giriniz' },
                            { max: 255, message: 'Başlık en fazla 255 karakter olabilir' },
                        ]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Açıklama"
                        name="description"
                        rules={[
                            { required: true, message: 'Lütfen duyuru açıklaması giriniz' },
                            { max: 1000, message: 'Açıklama en fazla 1000 karakter olabilir' },
                        ]}
                    >
                        <Input.TextArea />
                    </Form.Item>

                    <Form.Item
                        label="Duyuru Tipi"
                        name="type"
                        rules={[
                            { required: true, message: 'Lütfen duyuru tipini seçiniz' },
                        ]}
                    >
                        <Select placeholder="Duyuru tipini seçiniz">
                            <Option value="INFORMATION">Bilgilendirme</Option>
                            <Option value="UPDATE">Güncelleme</Option>
                            <Option value="EVENT">Etkinlik</Option>
                        </Select>
                    </Form.Item>

                    <Row justify="center">
                        <Popconfirm
                            title="Duyuruyu yayınlamak istediğinize emin misiniz?"
                            onConfirm={() => form.submit()}
                            onCancel={() => setIsModalVisible(false)}
                            okText="Evet"
                            cancelText="Hayır"
                        >
                            <Button
                                type="primary"
                                className="d-button dark-button width-auto"
                            >
                                Duyuruyu Yayınla
                            </Button>
                        </Popconfirm>
                    </Row>
                </Form>
            </Modal>
        </>
    );
};

export default AnnouncementList;
